import React from "react";
import { Modal, Form, Input, InputNumber, Select, Switch, Tabs, Button } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import type { RoomPackage } from "../../../types";

const CHARGE_BASIS_OPTIONS = [
  { label: "Per Room/Night", value: "per_room_per_night" },
  { label: "Per Person/Night", value: "per_person_per_night" },
  { label: "Per Stay", value: "per_stay" },
  { label: "Per Person/Stay", value: "per_person_per_stay" },
  { label: "Complimentary", value: "complimentary" },
];

const INCLUSION_TYPES = ["meal", "service", "amenity", "activity", "other"];

interface MenuCategory {
  id: string;
  name: string;
  is_active: boolean;
}

interface RoomPackageFormModalProps {
  open: boolean;
  editingPackage: RoomPackage | null;
  loading: boolean;
  form: any;
  menuCategories: MenuCategory[];
  onCancel: () => void;
  onOk: (values: any) => void;
}

const RoomPackageFormModal: React.FC<RoomPackageFormModalProps> = ({
  open,
  editingPackage,
  loading,
  form,
  menuCategories,
  onCancel,
  onOk,
}) => {
  const inclusions = Form.useWatch("inclusions", form) || [];
  return (
    <Modal
      title={editingPackage ? "Edit Room Package" : "Create Room Package"}
      open={open}
      onCancel={onCancel}
      width={680}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => onOk(form)}>
          {editingPackage ? "Update" : "Create"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" initialValues={{ is_active: true, sort_order: 0, charge_basis: "per_person_per_night", inclusions: [] }}>
        <Tabs
          defaultActiveKey="basic"
          items={[
            {
              key: "basic",
              label: "Basic Info",
              children: (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <Form.Item
                      name="code"
                      label="Code"
                      rules={[
                        { required: true, message: "Required" },
                        { max: 20, message: "Max 20 characters" },
                      ]}
                    >
                      <Input placeholder="e.g. RO, BB, HB" style={{ fontFamily: "monospace" }} />
                    </Form.Item>
                    <Form.Item name="sort_order" label="Sort Order">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input placeholder="e.g. Bed & Breakfast" />
                  </Form.Item>
                  <Form.Item name="description" label="Description">
                    <Input.TextArea rows={2} placeholder="Optional description" />
                  </Form.Item>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <Form.Item name="category" label="Category">
                      <Input placeholder="e.g. meal, premium" />
                    </Form.Item>
                    <Form.Item
                      name="charge_basis"
                      label="Charge Basis"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Select options={CHARGE_BASIS_OPTIONS} />
                    </Form.Item>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <Form.Item name="base_price" label="Base Price (LKR)">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="price_per_person" label="Price Per Person (LKR)">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                  <Form.Item name="is_active" label="Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </>
              ),
            },
            {
              key: "inclusions",
              label: "Inclusions",
              children: (
                <>
                  <Form.List name="inclusions">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => {
                          const isUnlimited = inclusions[name]?.is_unlimited;
                          return (
                            <div
                              key={key}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 80px 100px 40px",
                                gap: "0 8px",
                                alignItems: "start",
                                marginBottom: 8,
                              }}
                            >
                              <Form.Item
                                {...restField}
                                name={[name, "item_type"]}
                                rules={[{ required: true, message: "Type" }]}
                              >
                                <Select
                                  placeholder="Type"
                                  options={INCLUSION_TYPES.map((t) => ({ label: t, value: t }))}
                                  onChange={(value) => {
                                    if (value !== 'meal') {
                                      form.setFieldValue(['inclusions', name, 'item_ref_id'], null);
                                      form.setFieldValue(['inclusions', name, 'item_name'], undefined);
                                    }
                                  }}
                                />
                              </Form.Item>
                              {inclusions[name]?.item_type === 'meal' ? (
                                <Form.Item
                                  {...restField}
                                  name={[name, "item_name"]}
                                  rules={[{ required: true, message: "Required" }]}
                                >
                                  <Select
                                    placeholder="Select category"
                                    showSearch
                                    optionFilterProp="label"
                                    options={menuCategories
                                      .filter((c) => c.is_active)
                                      .map((c) => ({ label: c.name, value: c.name }))}
                                    onChange={(value) => {
                                      const cat = menuCategories.find((c) => c.name === value);
                                      form.setFieldValue(['inclusions', name, 'item_ref_id'], cat?.id || null);
                                    }}
                                  />
                                </Form.Item>
                              ) : (
                                <Form.Item {...restField} name={[name, "item_name"]}>
                                  <Input placeholder="Item name" />
                                </Form.Item>
                              )}
                              <Form.Item {...restField} name={[name, "item_ref_id"]} hidden>
                                <Input />
                              </Form.Item>
                              <Form.Item {...restField} name={[name, "quantity"]}>
                                <InputNumber min={1} placeholder="Qty" style={{ width: "100%" }} disabled={isUnlimited} />
                              </Form.Item>
                              <Form.Item {...restField} name={[name, "is_unlimited"]} valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch checkedChildren="Unlimited" unCheckedChildren="Limited" size="small" />
                              </Form.Item>
                              <MinusCircleOutlined
                                onClick={() => remove(name)}
                                style={{ marginTop: 10, color: "#ff4d4f" }}
                              />
                            </div>
                          );
                        })}
                        <Button type="dashed" onClick={() => add({ quantity: 1, is_unlimited: false })} block icon={<PlusOutlined />}>
                          Add Inclusion
                        </Button>
                      </>
                    )}
                  </Form.List>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};

export default RoomPackageFormModal;
